import React, { useState, useMemo, useEffect } from "react";
import { Search, Link } from "@mui/icons-material";
import { Pagination } from "@mui/material";

import About from "../About";
import MoreBadge from "../shared/MoreBadge.jsx";

import data from "../../../../_data/about.json";

export default function Members() {
  const members = data?.members;
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const filteredMembers = useMemo(() => {
    if (!members?.list) return [];
    return members.list.filter((m) => {
      const matchesSearch =
        !searchTerm ||
        m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.language || []).some((l) => l.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (m.skill || []).some((s) => s.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesFilters =
        activeFilters.length === 0 ||
        activeFilters.every(
          (f) => m.role === f || (m.language || []).includes(f) || (m.skill || []).includes(f),
        );

      const isActive = m.isActive !== false; // Default to true if isActive is not set

      return matchesSearch && matchesFilters && isActive;
    });
  }, [members, searchTerm, activeFilters]);

  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);

  const paginatedMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredMembers.slice(startIndex, endIndex);
  }, [filteredMembers, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilters]);

  if (!members) {
    return (
      <section className="members">
        <div className="members-empty">Members data not found.</div>
      </section>
    );
  }

  const handleClearFilters = () => {
    setActiveFilters([]);
    setSearchTerm("");
  };

  return (
    <>
      <About />

      <section className="members">
        <div className="members-header">
          <div className="members-toolbar">
            <p className="members-subtitle">{members.description}</p>

            {activeFilters.length > 0 && (
              <div className="members-filters-active">
                {activeFilters.map((f) => (
                  <div key={f} className="members-filter-chip">
                    {f}
                  </div>
                ))}
                <button className="members-clear-all" onClick={handleClearFilters}>
                  Clear all filters
                </button>
              </div>
            )}

            <div className="members-search-wrapper">
              <div className="members-search-box">
                <Search className="members-search-icon" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {filteredMembers.length === 0 ? (
          <div className="members-no-results">
            <div className="members-no-results-icon">
              <Search />
            </div>
            <p className="members-no-results-title">No members found</p>
            <p className="members-no-results-message">
              Try adjusting your search to find what you&apos;re looking for.
            </p>
            {(searchTerm || activeFilters.length > 0) && (
              <button className="members-clear-btn" onClick={handleClearFilters}>
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="members-grid">
            {paginatedMembers.map((member, i) => {
              const allSkills = [...(member.language || []), ...(member.skill || [])];
              const allProjects = [
                ...(member.assigned_projects || []),
                ...(member.past_projects || []),
              ];

              return (
                <div key={i} className="members-card">
                  <div className="members-card-header">
                    <div className="members-avatar">
                      {member.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .slice(0, 2)}
                    </div>
                    <div className="members-info">
                      <h3 className="members-name">{member.name}</h3>
                      <p className="members-role">{member.role || "Consultant"}</p>
                    </div>
                  </div>

                  <div className="members-section">
                    <h4 className="members-section-label">Skills</h4>
                    <div className="members-chips">
                      {allSkills.slice(0, 5).map((s) => (
                        <span key={s} className="members-chip-skill">
                          {s}
                        </span>
                      ))}
                      {allSkills.length > 5 && (
                        <MoreBadge
                          items={allSkills.slice(5)}
                          count={allSkills.length - 5}
                          type="skill"
                        />
                      )}
                    </div>
                  </div>

                  <div className="members-section">
                    <h4 className="members-section-label">Projects</h4>
                    <div className="members-chips">
                      {allProjects.slice(0, 4).map((p, pi) => (
                        <div key={pi} className="members-chip-project">
                          <Link className="members-project-icon" />
                          <span>{p}</span>
                        </div>
                      ))}
                      {allProjects.length > 4 && (
                        <MoreBadge
                          items={allProjects.slice(4)}
                          count={allProjects.length - 4}
                          type="project"
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="members-pagination">
            <div className="members-pagination-info">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, filteredMembers.length)} of{" "}
              {filteredMembers.length} members
            </div>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(e, page) => setCurrentPage(page)}
              size="medium"
              color="primary"
              showFirstButton
              showLastButton
            />
          </div>
        )}
      </section>
    </>
  );
}
